/**
 * Common Analytics Attributes
 * Utility functions to automatically extract common attributes for analytics events
 * These attributes are merged with event properties automatically
 */

import { getItem as getSessionItem } from '@/core/services/storage/utils/sessionStorageUtil';
import { getItem as getLocalItem } from '@/core/services/storage/utils/localStorageUtil';
import { getCookie } from '@/core/utils/cookieUtils';
import type { RootState } from '@/app/store/store';
import { storageBuild } from '@/core/services/storage/config/storage.build';
import { MULTIVARIATE_EXPERIMENT_CONFIG } from '@/shared/features/multivariateexperiment/config/multivariateExperimentConfig';

/**
 * User Info interface
 */
export interface UserInfo {
  isLoggedIn?: boolean;
  userId?: string;
  userCode?: string;
  email?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  customerName?: string;
  userName?: string;
  customerId?: string | number;
  visitorId?: string | number;
  pincode?: string;
  softLoginType?: string;
  guessedPincode?: boolean;
  country?: string;
  /** From `/user-context`; Meta CAPI `client_ip_address` only — never pixel. */
  client_ip?: string;
  [key: string]: unknown;
}

/**
 * User Info Getter Function
 * Allows custom implementations to provide user info from any source
 */
export type UserInfoGetter = () => UserInfo | null;

/**
 * Store reference for accessing Redux state
 * Set this when the store is available (e.g., in analytics initialization)
 */
let storeReference: { getState: () => RootState } | null = null;

/**
 * Custom user info getter function
 * If set, this takes precedence over default sources
 */
let customUserInfoGetter: UserInfoGetter | null = null;

/**
 * Set Redux store reference for accessing user state
 * Call this during analytics initialization if Redux store is available
 */
export function setStoreReference(store: { getState: () => RootState }): void {
  storeReference = store;
}

/**
 * Set custom user info getter
 * Allows providing user info from any custom source
 */
export function setUserInfoGetter(getter: UserInfoGetter): void {
  customUserInfoGetter = getter;
}

/**
 * Parse query parameters from URL search string
 */
function getQueryParams(search: string): Record<string, string> {
  const params: Record<string, string> = {};
  try {
    const urlParams = new URLSearchParams(search);
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
  } catch (error) {
    console.error('Error parsing query params:', error);
  }
  return params;
}

/**
 * Get page name from current URL
 * Determines page type based on URL patterns
 */
export function getPageName(): Record<string, string> {
  try {
    const attributes: Record<string, string> = {};

    if (typeof window === 'undefined') {
      return attributes;
    }

    const href = window.location.href;
    const pathname = window.location.pathname || '';

    if (href.includes('/search')) {
      attributes['Page Name'] = 'Search';
    } else if (href.includes('/jewellery')) {
      attributes['Page Name'] = 'PLP';
    } else if (href.includes('/stores/')) {
      attributes['Page Name'] = 'Stores';
    } else if (href.includes('/recently-viewed')) {
      attributes['Page Name'] = 'Recently Viewed';
    } else if (href.includes('~')) {
      attributes['Page Name'] = 'PDP';
    } else if (href.includes('/wishlist')) {
      attributes['Page Name'] = 'Wishlist';
    } else if (pathname === '/' || pathname === '') {
      attributes['Page Name'] = 'Home';
    }

    return attributes;
  } catch (error) {
    console.error('Error in getPageName:', error);
    return {};
  }
}

/**
 * Get URL parameters (UTM and search query)
 * Extracts UTM parameters and search query from URL
 */
export function getUrlParams(): Record<string, string> {
  try {
    const urlParams: Record<string, string> = {};

    if (typeof window === 'undefined') {
      return urlParams;
    }

    const href = window.location.href;
    if (href.indexOf('?') !== -1) {
      const qs = getQueryParams(window.location.search);
      
      if (qs.utm_source) {
        urlParams['UTM Source'] = qs.utm_source;
      }
      if (qs.utm_medium) {
        urlParams['UTM Medium'] = qs.utm_medium;
      }
      if (qs.utm_campaign) {
        urlParams['UTM Campaign'] = qs.utm_campaign;
      }
      if (qs.search_query) {
        urlParams['Search Query'] = qs.search_query;
      }
    }

    return urlParams;
  } catch (error) {
    console.error('Error in getUrlParams:', error);
    return {};
  }
}

/**
 * Get user info from Redux store
 * Checks common Redux state paths for user information
 */
function trimNamePart(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function mapUserContextRecordToUserInfo(record: Record<string, unknown>): UserInfo {
  const isLoggedIn = Boolean(record.isLoggedIn);
  const customerIdRaw = isLoggedIn ? (record.customerId ?? record.userCode) : record.customerId;
  const userCode =
    typeof record.userCode === 'string' && record.userCode.trim()
      ? record.userCode.trim()
      : undefined;
  return {
    ...record,
    isLoggedIn,
    userCode,
    firstName: trimNamePart(record.firstName),
    middleName: trimNamePart(record.middleName),
    lastName: trimNamePart(record.lastName),
    customerId:
      customerIdRaw != null && String(customerIdRaw).trim() !== ''
        ? String(customerIdRaw)
        : undefined,
    visitorId:
      record.visitorId != null && Number.isFinite(Number(record.visitorId))
        ? String(record.visitorId)
        : undefined,
    pincode: typeof record.pincode === 'string' ? record.pincode : undefined,
    softLoginType:
      typeof record.softLoginType === 'string' && record.softLoginType.trim()
        ? record.softLoginType.trim()
        : undefined,
    guessedPincode: typeof record.guessedPincode === 'boolean' ? record.guessedPincode : undefined,
    userId:
      customerIdRaw != null && String(customerIdRaw).trim() !== ''
        ? String(customerIdRaw)
        : record.visitorId != null
          ? String(record.visitorId)
          : undefined,
    client_ip:
      typeof record.client_ip === 'string' && record.client_ip.trim()
        ? record.client_ip.trim()
        : undefined,
  };
}

/**
 * Read `/user-context` from RTK Query cache (primary source in react-new).
 */
function getUserInfoFromUserContextApi(): UserInfo | null {
  if (!storeReference) {
    return null;
  }

  try {
    const state = storeReference.getState() as Record<string, unknown>;
    const apiState = state.userContextApi as
      | { queries?: Record<string, { data?: unknown }> }
      | undefined;
    const queries = apiState?.queries;
    if (!queries) {
      return null;
    }

    for (const entry of Object.values(queries)) {
      const data = entry?.data;
      if (!data || typeof data !== 'object') {
        continue;
      }
      const record = data as Record<string, unknown>;
      if (!('isLoggedIn' in record) && record.visitorId == null && record.customerId == null) {
        continue;
      }
      return mapUserContextRecordToUserInfo(record);
    }
  } catch (error) {
    console.error('Error getting user info from userContextApi:', error);
  }

  return null;
}

function getUserInfoFromRedux(): UserInfo | null {
  if (!storeReference) {
    return null;
  }

  try {
    const state = storeReference.getState();
    
    // Check common Redux state paths for user info
    // Adjust these paths based on your actual Redux structure
    const userState = 
      (state as Record<string, unknown>).user ||
      (state as Record<string, unknown>).auth ||
      (state as Record<string, unknown>).userInfo ||
      (state as Record<string, unknown>).session;

    if (userState && typeof userState === 'object') {
      const user = userState as Record<string, unknown>;
      return {
        isLoggedIn: Boolean(user.isLoggedIn ?? user.loggedIn ?? user.authenticated),
        userId: user.userId as string | undefined,
        email: user.email as string | undefined,
        ...user,
      } as UserInfo;
    }
  } catch (error) {
    console.error('Error getting user info from Redux:', error);
  }

  return null;
}

/**
 * Get user info from sessionStorage
 */
function getUserInfoFromSessionStorage(): UserInfo | null {
  try {
    return getSessionItem<UserInfo>('userinfo');
  } catch (error) {
    console.error('Error getting user info from sessionStorage:', error);
    return null;
  }
}

/**
 * Get user info from localStorage (fallback)
 */
function getUserInfoFromLocalStorage(): UserInfo | null {
  try {
    return getLocalItem<UserInfo>('userinfo');
  } catch (error) {
    console.error('Error getting user info from localStorage:', error);
    return null;
  }
}

/**
 * Get user info from multiple sources
 * Priority: Custom getter > userContextApi > legacy Redux > sessionStorage > localStorage
 */
export function getUserInfo(): UserInfo | null {
  // 1. Check custom getter first (highest priority)
  if (customUserInfoGetter) {
    try {
      const userInfo = customUserInfoGetter();
      if (userInfo) {
        return userInfo;
      }
    } catch (error) {
      console.error('Error in custom user info getter:', error);
    }
  }

  // 2. RTK Query `/user-context` cache (react-new primary source)
  const userContextApiInfo = getUserInfoFromUserContextApi();
  if (userContextApiInfo) {
    return userContextApiInfo;
  }

  // 3. Legacy Redux user/auth slices
  const reduxUserInfo = getUserInfoFromRedux();
  if (reduxUserInfo) {
    return reduxUserInfo;
  }

  // 4. Check sessionStorage
  const sessionUserInfo = getUserInfoFromSessionStorage();
  if (sessionUserInfo) {
    return sessionUserInfo;
  }

  // 5. Check localStorage (fallback)
  const localUserInfo = getUserInfoFromLocalStorage();
  if (localUserInfo) {
    return localUserInfo;
  }

  return null;
}


export function getWebEngageCommonAttributes(): Record<string, string | number | boolean | null | undefined> {
  const payload: Record<string, string | number | boolean | null | undefined> = {};
  try {
    const state = storeReference?.getState();
    const experimentState = state?.multivariateExperiment;
    payload[MULTIVARIATE_EXPERIMENT_CONFIG.HOME.eventAttributeKey] = experimentState?.home ?? 'NONE';
    // payload[MULTIVARIATE_EXPERIMENT_CONFIG.GOLD_COIN_PLP.eventAttributeKey] = experimentState?.goldCoinPlp ?? 'NONE';
    payload[MULTIVARIATE_EXPERIMENT_CONFIG.PLP.eventAttributeKey] = experimentState?.plp ?? 'NONE';
    payload[MULTIVARIATE_EXPERIMENT_CONFIG.PDP.eventAttributeKey] = experimentState?.pdp ?? 'NONE';
  } catch (error) {
    console.error('Error in getWebEngageCommonAttributes:', error);
    payload[MULTIVARIATE_EXPERIMENT_CONFIG.HOME.eventAttributeKey] = 'NONE';
    // payload[MULTIVARIATE_EXPERIMENT_CONFIG.GOLD_COIN_PLP.eventAttributeKey] = 'NONE';
    payload[MULTIVARIATE_EXPERIMENT_CONFIG.PLP.eventAttributeKey] = 'NONE';
    payload[MULTIVARIATE_EXPERIMENT_CONFIG.PDP.eventAttributeKey] = 'NONE';
  }
  return payload;
}

export function getCommonServerAttributes(): Record<string, string | number | boolean> {
  const payload: Record<string, string | number | boolean> = {};

  if (typeof window === 'undefined') {
    return payload;
  }

  try {
    const userInfo = getUserInfo();
    if (userInfo) {
      const customerId = userInfo.customerId != null ? String(userInfo.customerId) : '';
      const visitorId = userInfo.visitorId != null ? String(userInfo.visitorId) : '';
      payload.customerId = customerId;
      payload.visitorId = visitorId;
      payload.user_id = userInfo.isLoggedIn ? customerId || visitorId : visitorId || customerId;
      payload.user_type = userInfo.isLoggedIn ? 'logged_in_user' : 'guest_user';
    }

    const { repo: storageRepo } = storageBuild();
    const isPincodeUpdated = storageRepo.localGetData<boolean | string>('isPincodeUpdated');
    const isPincodeSet =
      isPincodeUpdated === true ||
      (typeof isPincodeUpdated === 'string' &&
        isPincodeUpdated.trim().toLowerCase() === 'true');
    if (isPincodeSet) {
      const pincode = storageRepo.localGetData<string>('pincode');
      payload.pincode =
        typeof pincode === 'string' && pincode.trim() ? pincode.trim() : '';
    } else {
      payload.pincode = '';
    }

    payload.referrer = '';
    payload.directServer = 0;
    payload.client_user_agent = navigator.userAgent || '';
    payload._fbp = getCookie('_fbp') || '';
    payload._fbc = getCookie('_fbc') || '';
    payload.isApp = false;
    payload.platform = window.innerWidth >= 768 ? 'DESKTOP' : 'MOBILE';
    payload.source = 'online';
    payload.eventSource = 'online';
    payload.timestamp = Date.now();
    payload.currentURL = window.location.href;
  } catch (error) {
    console.error('Error in getCommonServerAttributes:', error);
  }

  return payload;
}

export function getCommonClientAttributes(): Record<string, string | number | boolean | null | undefined> {
  const eventLoggerPayload: Record<string, string | number | boolean | null | undefined> = {};

  try {
    // Get user info from multiple sources (Redux, sessionStorage, localStorage)
    const userInfo = getUserInfo();
    // Always include Logged In (default false) so every event has a consistent shape.
    // Note: Some providers (e.g. GA4) require normalized parameter keys; that mapping
    // is handled in the provider adapter layer.
    eventLoggerPayload['Logged In'] = userInfo?.isLoggedIn ?? false;
    eventLoggerPayload['Soft Login Type'] =
      typeof userInfo?.softLoginType === 'string' && userInfo.softLoginType.trim()
        ? userInfo.softLoginType.trim()
        : 'None';

    // Merge page name and URL params
    const pageName = getPageName();
    const urlParams = getUrlParams();

    return {
      ...eventLoggerPayload,
      ...pageName,
      ...urlParams,
      'Variation': 'React',
    };
  } catch (error) {
    console.error('Error in getCommonClientAttributes:', error);
    return eventLoggerPayload;
  }
}

